import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ZoomContainerComponent } from '../zoom/zoom-container/zoom-container.component';
import { SVG_PIN_BLACK, SVG_RED, SvgHelper } from './svg.helper';
import { Zoom } from './svg.zoom';
import { TranslocoPipe } from '@jsverse/transloco';
import { Rect, SVG, Element as SVGElement, Svg } from '@svgdotjs/svg.js';
import { Button } from 'primeng/button';
import { Tooltip, TooltipModule } from 'primeng/tooltip';

type SvgData = {
  sourceText: string;
  type: string;
};

export type SvgType = 'haynesPro' | 'vw';

@Component({
  selector: 'app-svg',
  templateUrl: './svg.component.html',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, TooltipModule, ZoomContainerComponent, Button],
})
export class SvgComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
  url = input<string | null | undefined>();
  padding = input(0);
  maxHeight = input<string | undefined>();
  maxWidth = input<string | undefined>();
  withZoom = input(false);
  svgType = input<SvgType | undefined>();

  viewInitialized = false;
  renderingStarted = false;

  private _renderingCompleteResolve?: () => void;
  public renderingComplete: Promise<boolean> = Promise.resolve(true);

  objectType = signal<'svg' | 'img' | undefined>(undefined);
  svgData: SvgData | undefined;
  svg: Svg | undefined;
  points: SVGElement[] = [];
  zoom: ReturnType<typeof Zoom> | undefined;
  minZoom = 0.3;
  maxZoom = 5;
  svgHelper: ReturnType<typeof SvgHelper> | undefined;
  tooltipContent = '';
  hasError = signal(false);

  @ViewChild(Tooltip) private pTooltip!: Tooltip;
  private _svgZoomContainer = viewChild<ElementRef<HTMLDivElement>>('svgZoomContainer');
  private _svgContainer = viewChild<ElementRef<HTMLDivElement>>('svgContainer');
  private _imgContainer = viewChild<ElementRef<HTMLDivElement>>('imgContainer');
  private _tooltipProxy = viewChild<ElementRef<HTMLDivElement>>('tooltipProxy');

  private _highlightElement: Rect | undefined;
  private _tooltipListeners: { element: SVGElement; type: string; listener: EventListener }[] = [];
  private _allWiringGroups: SVGElement[] = [];
  private _callStack: { func: any; args: any[] }[] = [];

  constructor() {
    this._initRenderingPromise();
  }

  // get isZoomed(): boolean {
  //   return (this.zoom?.currentZoom() ?? 1) !== 1;
  // }
  isZoomed = computed(() => (this.zoom?.currentZoom() ?? 1) !== 1);

  init = effect(async () => {
    const url = this.url();
    if (!url) {
      this.viewInitialized = true;
      this.renderingStarted = false;
      this.hasError.set(true);
      this._initRenderingPromise();
      return;
    }

    // Reset state before loading new SVG
    this.svg = undefined;
    this._resetTooltip();
    this._removeWiringGroupListeners();
    this.hasError.set(false);
    this._initRenderingPromise();

    this.svgData = await this._loadSvg(url);

    if (this.viewInitialized && !this.renderingStarted) {
      this.render(this.svgData);
    }
  });

  ngAfterViewInit(): void {
    this.viewInitialized = true;
  }

  ngAfterViewChecked(): void {
    if (this.svgData && !this.svg && !this.renderingStarted) {
      this.render(this.svgData);
    }
  }

  ngOnDestroy(): void {
    this._resetTooltip();
    this._removeWiringGroupListeners();
    this.svgHelper = undefined;
    this.zoom?.cleanupListeners();
    this.hasError.set(false);
  }

  async render(svgData: SvgData | undefined) {
    if (!svgData) return;

    if (this.renderingStarted) {
      console.warn('[SvgComponent] Render already started - skipping duplicate call.');
      return;
    }

    const { sourceText, type } = svgData;
    const url = this.url();

    const host = this._svgContainer()?.nativeElement;
    if (host) {
      host.innerHTML = ''; // Safely clear only the dynamic content
    }

    if (type.startsWith('image/svg')) {
      this.objectType.set('svg');
      window.setTimeout(() => {
        this._renderSvg(sourceText);
        this.afterImageRender();
      }, 2);
    } else if (type.startsWith('image/')) {
      this.objectType.set('img');
      window.setTimeout(() => {
        this._renderImage(url, this.padding());
        this.afterImageRender();
      }, 2);
    } else {
      console.error('[SvgComponent] Given file data has no valid type:', type, url);
    }
  }

  afterImageRender() {
    if (!this.svg) return;

    this._processCallStack();
    this._initTooltipElements();
    this._initWiringHover();
    this.svgHelper?.preventTextSelection(this.svg);
    this._initZoom();

    // Resolve the rendering promise
    this._completeRendering();
  }

  public hasComponent = (elementId: string): boolean => {
    if (!this.svg) return false;
    const elementsToHighlight = this.svgType() === 'vw' ? this._getComponentsInVwSvg(elementId) : this._getComponentsInHaynesProSvg(elementId);
    return elementsToHighlight.length > 0;
  };

  public highlightSvgComponent = (elementId: string, withBorder = false): Rect[] => {
    const elementsToHighlight = this.svgType() === 'vw' ? this._getComponentsInVwSvg(elementId) : this._getComponentsInHaynesProSvg(elementId);
    const highlightRects: Rect[] = [];
    elementsToHighlight.forEach((el: any) => {
      const rect = this.svgHelper?.highlightElement(this.svg, el, withBorder);
      if (rect) {
        highlightRects.push(rect);
      }
    });
    return highlightRects;
  };

  public clearComponentHighlight = (highlightElement: Rect | undefined) => {
    if (highlightElement) {
      highlightElement.remove();
      this.svgHelper?.resetOrigStrokeColor(highlightElement);
    }
  };

  public highlightPin = (pinId: string) => {
    const elementSelector = `rect[id=pin-${pinId}]`;
    let highlightElement: Rect | undefined;
    if (this.svg) {
      const list = this.svg.find(elementSelector);
      list.forEach((el: any) => {
        highlightElement = this.svgHelper?.highlightElement(this.svg, el, true);
      });
    }
    return highlightElement;
  };

  public clearPinHighlight = (pinId: string) => {
    const highlightedSelector = `rect[data-highlight-of=pin-${pinId}]`;
    if (this.svg) {
      const list = this.svg.find(highlightedSelector);
      list.forEach((el: any) => {
        if (el) {
          el.remove();

          this.svgHelper?.resetOrigStrokeColor(el);
        }
      });
    }
  };

  /**
   * Display a connector from the parent component.
   * @ViewChild(SvgComponent) svgComponent!: SvgComponent;
   * this.svgComponent.showConnector('345100416', 'black');
   */
  public showConnector = (pinId: string, color: 'red' | 'black' = 'red') => {
    if (!this.svg) {
      console.warn('[SvgComponent] SVG not yet initialized - keep Action in call stack.');
      this._callStack.push({ func: this.showConnector, args: [pinId, color] });
      return;
    }
    this.removePin(pinId);
    const colorCode = color === 'red' ? SVG_RED : SVG_PIN_BLACK;
    const pinElement = SVG(`#pin${pinId}`);
    if (pinElement) {
      const bbox = pinElement.rbox(this.svg);
      const point = this.svgHelper?.drawPoint(this.svg, bbox.x, bbox.y, colorCode);
      if (point) {
        point.data('pinId', pinId);
        this.points.push(point);
        setTimeout(() => this.svgHelper?.redraw(this.svg), 0);
      }
    }
  };

  public removeAllPins = (): void => {
    const pointsToRemove = [...this.points];
    let anyRemoved = false;

    pointsToRemove.forEach((point) => {
      if (this.svgHelper?.removePoint(point)) {
        anyRemoved = true;
      }
    });

    this.points = [];

    // Only force redraw if points were actually removed
    if (anyRemoved) {
      if (pointsToRemove.length > 0) {
        pointsToRemove.forEach((point) => {
          this.svgHelper?.removePoint(point);
        });
        this.points = [];
        this.svgHelper?.redraw(this.svg);
      }
    }
  };

  public removePin = (pinId: string): void => {
    const existingPointIndex = this.points.findIndex((p) => p?.data('pinId') === pinId);
    if (existingPointIndex >= 0) {
      const point = this.points[existingPointIndex];
      this.svgHelper?.removePoint(point);
      this.points.splice(existingPointIndex, 1);
    }
  };

  public resetZoom = (): void => {
    this.zoom?.reset();
  };

  private _renderImage = (url: string | null | undefined, padding: number): void => {
    if (!url) return;
    const host = this._imgContainer()?.nativeElement;
    if (!host) return;

    this.renderingStarted = true;

    const img = document.createElement('img');
    img.src = url;
    img.style.width = '100%';
    img.style.height = '100%';
    img.classList.add('object-contain');
    const maxWidth = this.maxWidth();
    if (maxWidth) img.style.maxHeight = maxWidth;
    const maxHeight = this.maxHeight();
    if (maxHeight) img.style.maxHeight = maxHeight;
    host.appendChild(img);
    host.style.padding = `${padding}px`;
  };

  private _renderSvg(svgText?: string): void {
    if (!svgText) return;
    const zoomContainer = this._svgZoomContainer()?.nativeElement;
    const svgContainer = this._svgContainer()?.nativeElement;
    if (!zoomContainer || !svgContainer || !zoomContainer.clientWidth) {
      this.viewInitialized = false;
      // console.error('[SvgComponent] Missing clientWidth - SVG', container);
      return;
    }

    this.renderingStarted = true;

    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, 'image/svg+xml');
    const svgEl = doc.documentElement;

    this.svgHelper = SvgHelper(svgEl as unknown as Svg);
    this.svgHelper?.removeAllJs(svgEl);
    this.svgHelper?.replaceGenericIds(svgEl, Math.random().toString(36).slice(2));

    svgContainer.appendChild(svgEl);

    const svg = SVG(svgEl) as Svg;
    const viewbox = svg.node.hasAttribute('viewBox') ? svg.viewbox() : svg.bbox();
    const padding = this.padding();
    const unitsPerPixelX = viewbox.width / zoomContainer.clientWidth;
    const unitsPerPixelY = viewbox.height / zoomContainer.clientHeight;
    const paddingX = padding * unitsPerPixelX;
    const paddingY = padding * unitsPerPixelY;
    const newWidth = viewbox.width + paddingX * 2;
    const newHeight = viewbox.height + paddingY * 2;
    const newX = viewbox.x - paddingX;
    const newY = viewbox.y - paddingY;

    svg.viewbox(newX, newY, newWidth, newHeight);
    svg.size('100%', '100%');
    if (this.maxHeight()) svg.attr('style', `max-height: ${this.maxHeight()}`);
    if (this.maxWidth()) svg.attr('style', `max-width: ${this.maxWidth()}`);
    this.svg = svg;
  }

  private _initZoom(): void {
    if (!this.withZoom() || !this.svg?.node) return;
    this.zoom = Zoom({ minZoom: this.minZoom, maxZoom: this.maxZoom });
    this.zoom?.init(this.svg);
  }

  private _processCallStack = (): void => {
    this._callStack.forEach((item) => item.func(...item.args));
    this._callStack = [];
  };

  private _initWiringHover = (): void => {
    if (!this.svg) return;
    const wiringGroupSelector = 'g[id^="wireGroup"]';
    this._allWiringGroups = this.svg.find(wiringGroupSelector);
    this._allWiringGroups.forEach((el: SVGElement) => {
      el.on('mouseenter', () => this._onWiringGroupHover(el));
      el.on('mouseleave', () => this._onWiringGroupHoverOut(el));
    });
  };

  private _removeWiringGroupListeners = (): void => {
    this._allWiringGroups.forEach((el: SVGElement) => {
      el.off('mouseenter');
      el.off('mouseleave');
    });
    this._allWiringGroups = [];
  };

  private _onWiringGroupHover = (wiringGroupElement: SVGElement): void => {
    wiringGroupElement.find('line, path').forEach((wire: SVGElement) => {
      this.svgHelper?.changeStrokeColor(wire, SVG_RED);
    });
  };

  private _onWiringGroupHoverOut = (wiringGroupElement: SVGElement): void => {
    wiringGroupElement.find('line, path').forEach((wire: SVGElement) => {
      this.svgHelper?.resetOrigStrokeColor(wire);
    });
  };

  private _initTooltipElements = (): void => {
    if (!this.svg) return;
    const tooltipSelector = "image[id^='ecomponent'], rect[id^='controlunit']";
    // const tooltipSelector = '[description]';
    // const tooltipSelector = '[data-search]';

    /**
     * Text elements sit on top of larger components, which contain descriptions.
     * They prevent the mouseover events on the underlying components, with the
     * effect that the tooltip does not shows up.
     * This function sets the pointer-events to none, so that the mouseover events
     * can pass through the text elements to the underlying components.
     */
    this.svgHelper?.makeTextsSharpAndNotInteractive(this.svg);
    const elements = this.svg.find(tooltipSelector);
    elements.forEach((el: SVGElement) => {
      el.attr({ 'pointer-events': 'visiblePainted' });
      const mouseEnterListener = (event: Event) => this._onElementMouseEnter(event, el);
      const mouseLeaveListener = (event: Event) => this._onElementMouseLeave(event, el);
      el.node.addEventListener('mouseenter', mouseEnterListener);
      el.node.addEventListener('mouseleave', mouseLeaveListener);
      this._tooltipListeners.push({ element: el, type: 'mouseenter', listener: mouseEnterListener });
      this._tooltipListeners.push({ element: el, type: 'mouseleave', listener: mouseLeaveListener });
    });
  };

  private _resetTooltip = (): void => {
    this._tooltipListeners.forEach(({ element, type, listener }) => {
      element.node.removeEventListener(type, listener);
    });
    this._tooltipListeners = [];
  };

  private _onElementMouseEnter = (event: Event, element: SVGElement): void => {
    const proxyEl = this._tooltipProxy()?.nativeElement;
    const zoomContainer = this._svgZoomContainer()?.nativeElement;
    const targetEl = event.currentTarget as HTMLElement;
    if (!proxyEl || !zoomContainer || !targetEl) return;

    if (this._highlightElement) this._highlightElement.remove();
    this._highlightElement = this.svgHelper?.highlightElement(this.svg, element);

    const containerRect = zoomContainer.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const top = targetRect.top - containerRect.top;
    const left = targetRect.left - containerRect.left;

    proxyEl.style.top = `${top}px`;
    proxyEl.style.left = `${left}px`;
    proxyEl.style.width = `${targetRect.width}px`;
    proxyEl.style.height = `${targetRect.height}px`;

    this.tooltipContent = element.attr('description') || null;
    if (this.tooltipContent) {
      this.pTooltip.show();
    }
  };

  private _onElementMouseLeave = (event: Event, element: SVGElement): void => {
    if (this._highlightElement) {
      this._highlightElement.remove();
      this._highlightElement = undefined;
      this.svgHelper?.resetOrigStrokeColor(element);
    }
    this.pTooltip.hide();
  };

  private _loadSvg(url: string): Promise<SvgData | undefined> {
    return fetch(url)
      .then(async (res) => {
        const contentType = res.headers.get('Content-Type') || '';
        const sourceText = await res.text();
        return { sourceText, type: contentType };
      })
      .catch((error) => {
        console.error('[SvgComponent] Error loading SVG from URL:', url, error);
        this.hasError.set(true);
        return { sourceText: '', type: 'image/' };
      });
  }

  private _getComponentsInVwSvg = (componentId: string): SVGElement[] => {
    const foundElements: SVGElement[] = [];
    if (this.svg) {
      this.svg
        .find('metadata')
        .filter((el: any) => {
          try {
            return el.node.textContent === `id=${componentId}`;
          } catch {
            return false;
          }
        })
        .map((el: any) => {
          const parent = el.parent();
          if (parent) {
            foundElements.push(parent as SVGElement);
          }
        });
      return foundElements;
    }
    return foundElements;
  };

  private _getComponentsInHaynesProSvg = (componentId: string): SVGElement[] => {
    const elementSelector = `image[id=ecomponent${componentId}], rect[id=controlunit${componentId}], rect[id=pin-${componentId}]`;
    const foundElements: SVGElement[] = [];
    if (this.svg) {
      this.svg.find(elementSelector).map((el) => foundElements.push(el));
      return foundElements;
    }
    return foundElements;
  };

  private _initRenderingPromise(): void {
    this.renderingComplete = new Promise<boolean>((resolve) => {
      this._renderingCompleteResolve = () => resolve(true);
    });
  }

  private _completeRendering(): void {
    if (this._renderingCompleteResolve) {
      this._renderingCompleteResolve();
    }
  }
}
